import { FeedId } from 'features/streaming/types';
import { User, UserId, UserName } from 'features/users/types';
import { FeedStreamInfo, IceServers } from 'utils/webrtc';

export type SignalingSendEventType =
  | 'ping'
  | 'joinRoom'
  | 'startStream'
  | 'stopStream'
  | 'startScreenshare'
  | 'stopScreenshare'
  | 'stopRemoteScreenshare'
  | 'startedTalking'
  | 'stoppedTalking'
  | 'audioOnlyModeChanged'
  | 'endSession'
  | 'leaveSession'
  | 'muteRemoteAudio'
  | 'askToUnmuteRemoteAudio'
  | 'log'
  | 'muteRemoteVideo'
  | 'askToUnmuteRemoteVideo'
  | 'requestRecording'
  | 'recordingStarted'
  | 'recordingStopped'
  | 'stopRecordingRequest'
  | 'recordingFailure'
  | 'restreamingStarted'
  | 'requestRestreaming'
  | 'stopRestreamingRequest'
  | 'restreamingFailure'
  | 'restreamingStopped'
  | 'sendChatMessage'
  | 'deleteChatMessage'
  | 'deleteChatImage'
  | 'clearPublicChat'
  | 'toggleChatReaction'
  | 'raiseHand'
  | 'lowerHand'
  | 'kickUser'
  | 'kickUsers'
  | 'changeSpokenLanguage'
  | 'changeDisplayLanguage'
  | 'allowBroadcast'
  | 'disallowBroadcast'
  | 'bulkAllowBroadcast'
  | 'bulkDisallowBroadcast'
  | 'bulkAllowScreenshare'
  | 'bulkDisallowScreenshare'
  | 'allowScreenshare'
  | 'disallowScreenshare'
  | 'allowEditWhiteboard'
  | 'disallowEditWhiteboard'
  | 'bulkAllowEditWhiteboard'
  | 'bulkDisallowEditWhiteboard'
  | 'allowPresentContent'
  | 'disallowPresentContent'
  | 'bulkAllowPresentContent'
  | 'bulkDisallowPresentContent'
  | 'changeRole'
  | 'shareEncryptionKey'
  | 'roomNotFound'
  | 'lockRoom'
  | 'unlockRoom'
  | 'admitUserAccess'
  | 'rejectUserAccess'
  | 'bulkAdmitUserAccess'
  | 'bulkRejectUserAccess'
  | 'shareEncryptedName'
  | 'pushContent'
  | 'unlockContent'
  | 'updateLockedContentZoom'
  | 'changeWhiteboardMode'
  | 'openWhiteboard'
  | 'closeWhiteboard'
  | 'createWhiteboard'
  | 'restoreWhiteboard'
  | 'updateWhiteboard'
  | 'updateWhiteboardCursor'
  | 'updateContentWhiteboard'
  | 'updateContentWhiteboardLaser'
  | 'updateScreenshareWhiteboard'
  | 'updateScreenshareWhiteboardLaser'
  | 'shareWhiteboardKey'
  | 'requestWhiteboardKey'
  | 'requestWhiteboardData'
  | 'requestContentWhiteboardData'
  | 'requestScreenshareWhiteboardData'
  | 'bulkMuteRemoteAudio'
  | 'bulkMuteRemoteVideo'
  | 'requestFileUpload'
  | 'createBreakoutRooms'
  | 'editBreakoutRooms'
  | 'clearBreakoutRooms'
  | 'deleteBreakoutRoom'
  | 'addBreakoutRoom'
  | 'renameBreakoutRoom'
  | 'startBreakoutRooms'
  | 'stopBreakoutRooms'
  | 'assignUsersToBreakoutRoom'
  | 'unassignUserFromBreakoutRoom'
  | 'broadcastBreakoutRoomMessage'
  | 'requestBreakoutModerator'
  | 'askQuestion'
  | 'answerQuestion'
  | 'editQuestion'
  | 'editAnswer'
  | 'removeUpvoteQuestion'
  | 'upvoteQuestion'
  | 'deleteQuestion'
  | 'deleteAnswer'
  | 'startLiveAnswerQuestion'
  | 'stopLiveAnswerQuestion'
  | 'cancelLiveAnswerQuestion'
  | 'dismissQuestion'
  | 'reopenQuestion'
  | 'disableFeature'
  | 'enableFeature'
  | 'openFile'
  | 'closeFile'
  | 'createPoll'
  | 'launchPoll'
  | 'endPoll'
  | 'deletePoll'
  | 'votePoll'
  | 'sharePollResults'
  | 'stopSharingPollResults'
  | 'editPoll'
  | 'requestDetailedPollResults'
  | 'createPollTemplate'
  | 'editPollTemplate'
  | 'deletePollTemplate'
  | 'createQuiz'
  | 'launchQuiz'
  | 'endQuiz'
  | 'deleteQuiz'
  | 'editQuiz'
  | 'cloneQuiz'
  | 'submitQuizQuestion'
  | 'finishQuiz'
  | 'requestQuizResults'
  | 'createQuizTemplate'
  | 'editQuizTemplate'
  | 'deleteQuizTemplate'
  | 'requestDetailedQuizResults'
  | 'adjustAspectRatio'
  | 'openCaptionsPanel'
  | 'closeCaptionsPanel'
  | 'requestDownloadToken'
  | 'requestUploadToken'
  | 'startTranscription'
  | 'stopTranscription'
  | 'createLibraryFolder'
  | 'renameLibraryFolder'
  | 'deleteLibraryFolder'
  | 'renameLibraryFile'
  | 'moveLibraryFile'
  | 'moveLibraryFolder'
  | 'deleteLibraryFile'
  | 'requestLibraryFileUpload'
  | 'requestVirtualBackgroundUpload'
  | 'deleteVirtualBackground'
  | 'openLibraryFile'
  | 'closeLibraryFile'
  | 'shareLibraryFile'
  | 'unshareLibraryFile'
  | 'changeLibraryFilePage'
  | 'copyLibraryFile'
  | 'openPictureInPictureWindow'
  | 'closePictureInPictureWindow'
  | 'createLibraryWebapp'
  | 'downloadLibraryFile'
  | 'changeMediaPlayerState'
  | 'connectToPhoneBridge'
  | 'disconnectFromPhoneBridge'
  | 'askToUnmutePhoneUser'
  | 'mutePhoneUser'
  | 'phoneUserLowerHand'
  | 'requestMediaPlayerState'
  | 'banUser'
  | 'trackUsage'
  | 'requestInvitationLink'
  | 'sendLobbyMessage'
  | 'clearContent'
  | 'bulkCloseContent'
  | 'createRecordingBookmark'
  | 'deleteAvatar'
  | 'sendRoomReactions'
  | 'renameUser'
  | 'changePinnedPanels'
  | 'sendInvitationEmails'
  | 'virtualBackgroundApplied'
  | 'virtualBackgroundRemoved';

export type SignalingReceivedEventType =
  | 'connected'
  | 'pong'
  | 'streamStarted'
  | 'streamStopped'
  | 'screenshareStarted'
  | 'screenshareStopped'
  | 'screenshareStoppedRemotely'
  | 'externalStreamStarted'
  | 'externalStreamStopped'
  | 'disconnect'
  | 'roomJoined'
  | 'userJoined'
  | 'userLeft'
  | 'activeSpeakerChanged'
  | 'speakerStoppedTalking'
  | 'sessionEnded'
  | 'sessionLeft'
  | 'participantsLimitReached'
  | 'audioMutedRemotely'
  | 'askedToUnmuteAudioRemotely'
  | 'videoMutedRemotely'
  | 'askedToUnmuteVideoRemotely'
  | 'recordingStarted'
  | 'recordingRequested'
  | 'recordingStopRequested'
  | 'recordingFailure'
  | 'recordingStopped'
  | 'recorderJoined'
  | 'restreamingStarted'
  | 'restreamingRequested'
  | 'restreamingStopRequested'
  | 'restreamingFailure'
  | 'restreamingStopped'
  | 'restreamerJoined'
  | 'chatMessageSent'
  | 'chatMessageDeleted'
  | 'chatImageDeleted'
  | 'chatMessageUploadPrepared'
  | 'allChatMessagesReceived'
  | 'publicChatCleared'
  | 'chatReactionToggled'
  | 'speechCaptioned'
  | 'handRaised'
  | 'handLowered'
  | 'kicked'
  | 'broadcastAllowed'
  | 'broadcastDisallowed'
  | 'screenshareAllowed'
  | 'screenshareDisallowed'
  | 'roleChanged'
  | 'systemUpdate'
  | 'sessionNotStarted'
  | 'encryptionKeyShared'
  | 'entryAccessRequired'
  | 'roomLocked'
  | 'roomUnlocked'
  | 'waitingUserJoined'
  | 'waitingUserLeft'
  | 'waitingUserLeftBatch'
  | 'waitingUsersUpdated'
  | 'bulkUserAccessRejected'
  | 'accessRejected'
  | 'publicKeyShared'
  | 'encryptedNameShared'
  | 'whiteboardOpened'
  | 'whiteboardUpdated'
  | 'whiteboardClosed'
  | 'whiteboardCreated'
  | 'whiteboardModeChanged'
  | 'privateOpenedWhiteboardsReceived'
  | 'whiteboardCursorUpdated'
  | 'contentWhiteboardLaserUpdated'
  | 'editWhiteboardAllowed'
  | 'editWhiteboardDisallowed'
  | 'presentContentAllowed'
  | 'presentContentDisallowed'
  | 'whiteboardAlreadyOpened'
  | 'whiteboardDataReceived'
  | 'whiteboardDataRequestRejected'
  | 'whiteboardKeyShared'
  | 'whiteboardKeyRequested'
  | 'contentWhiteboardUpdated'
  | 'contentWhiteboardDataReceived'
  | 'screenshareWhiteboardUpdated'
  | 'screenshareWhiteboardDataReceived'
  | 'screenshareWhiteboardLaserUpdated'
  | 'roomJoinPaused'
  | 'broadcasterLimitReached'
  | 'screenshareLimitReached'
  | 'breakoutRoomsCreated'
  | 'breakoutRoomsEdited'
  | 'breakoutRoomsReceived'
  | 'breakoutRoomsCleared'
  | 'breakoutRoomDeleted'
  | 'breakoutRoomRenamed'
  | 'breakoutRoomsStarted'
  | 'breakoutRoomsStopped'
  | 'usersAssignedToBreakoutRoom'
  | 'userUnassignedFromBreakoutRoom'
  | 'fileUploadPrepared'
  | 'breakoutRoomMessageBroadcasted'
  | 'breakoutModeratorRequested'
  | 'breakoutUserJoined'
  | 'breakoutUserLeft'
  | 'breakoutUserLeftBatch'
  | 'breakoutRoomAdded'
  | 'questionEdited'
  | 'questionAsked'
  | 'questionAnswered'
  | 'questionUpvoted'
  | 'questionUpvoteRemoved'
  | 'questionDeleted'
  | 'answerDeleted'
  | 'answerEdited'
  | 'questionLiveAnswerStopped'
  | 'questionLiveAnswerCancelled'
  | 'questionLiveAnswerStarted'
  | 'questionDismissed'
  | 'questionReopened'
  | 'questionsReceived'
  | 'featureDisabled'
  | 'featureEnabled'
  | 'userLeftBatch'
  | 'pollEnded'
  | 'pollLaunched'
  | 'pollCreated'
  | 'pollsReceived'
  | 'pollDeleted'
  | 'pollVoted'
  | 'pollResultsShared'
  | 'pollResultsSharingStopped'
  | 'pollEdited'
  | 'pollTemplatesReceived'
  | 'pollTemplateCreated'
  | 'pollTemplateEdited'
  | 'pollTemplateDeleted'
  | 'quizCreated'
  | 'quizLaunched'
  | 'quizEnded'
  | 'quizDeleted'
  | 'quizEdited'
  | 'quizzesReceived'
  | 'quizFinished'
  | 'quizTemplatesReceived'
  | 'quizTemplateCreated'
  | 'quizTemplateEdited'
  | 'quizTemplateDeleted'
  | 'aspectRatioAdjusted'
  | 'sessionEndsSoon'
  | 'transcriptionStarted'
  | 'transcriptionStopped'
  | 'libraryReceived'
  | 'libraryFolderCreated'
  | 'libraryFolderRenamed'
  | 'libraryFolderDeleted'
  | 'libraryFileRenamed'
  | 'libraryFileMoved'
  | 'libraryFolderMoved'
  | 'libraryFileDeleted'
  | 'libraryFileUploaded'
  | 'libraryFileUploadAccepted'
  | 'virtualBackgroundUploadAccepted'
  | 'virtualBackgroundUploadRejected'
  | 'virtualBackgroundUploaded'
  | 'virtualBackgroundDeleted'
  | 'libraryFileOpened'
  | 'libraryFileClosed'
  | 'libraryFileConverted'
  | 'libraryFileConversionFailed'
  | 'libraryFileConversionProgress'
  | 'libraryFilePageChanged'
  | 'libraryOpenedFilesReceived'
  | 'libraryWebappCreated'
  | 'libraryFileShared'
  | 'libraryFileUnshared'
  | 'libraryFileCopied'
  | 'sharedLibraryFilesReceived'
  | 'mediaPlayerStateChanged'
  | 'contentPushed'
  | 'contentUnlocked'
  | 'lockedContentZoomUpdated'
  | 'phoneStreamStarted'
  | 'phoneBridgeDisconnected'
  | 'phoneBridgeConnecting'
  | 'phoneBridgeConnected'
  | 'phoneUserMuted'
  | 'phoneUserUnmuted'
  | 'phoneUsersJoined'
  | 'phoneUsersLeft'
  | 'phoneUserHandRaised'
  | 'phoneUserHandLowered'
  | 'phoneUserAskedToUnmute'
  | 'avatarChanged'
  | 'avatarDeleted'
  | 'sharedNotesReceived'
  | 'roomReactionsReceived'
  | 'banned'
  | 'lobbyMessageReceived'
  | 'contentCleared'
  | 'bulkContentClosed'
  | 'recordingBookmarkCreated'
  | 'userRenamed'
  | 'pinnedPanelsChanged'
  | 'spokenLanguageChanged';

export interface SignalingEventPayload<Data = any> {
  data: Data;
  initiator: EventUser;
}

export interface SignalingSendMessage {
  event: SignalingSendEventType;
  requestId?: string;
  data?: any;
}

export interface SignalingReceivedMessage {
  event: SignalingReceivedEventType;
  requestId?: string;
  success?: boolean;
  data?: any;
}

export interface SignalingPong {
  token: string;
  publishingMediaToken?: string;
  subscribingMediaToken?: string;
  sharedNotesToken?: string;
  // Present when signaling renews TURN credentials; validated before it replaces the stored list.
  iceServers?: IceServers;
}

// Absent on the 25 s keepalive; `true` asks the server to mint a fresh media uniqueId.
export interface SignalingPingData {
  refresh?: true;
}

export interface SystemUpdatePayload {
  token: string;
}

export type SignalingRoomUser = Required<User> &
  Partial<{
    screensharing: boolean;
    screenshareFeedId: FeedId;
    screenshareStreams: FeedStreamInfo[];
    startScreenshareTimestamp: number;
    pushedScreenshareTimestamp: number;
  }>;

export enum CloseCodes {
  NO_RECONNECT = 4000,
  INTERNAL_ERROR = 4001,
  SYSTEM_KICK = 4002,
  TERMINATED = 4999,
}

export interface EventUser {
  id: UserId;
  name?: UserName;
  avatarColor?: string;
  avatarUrl?: string;
  initials?: string;
  externalId?: string;
}

export interface EntityIdPayload {
  id: string;
}

export interface SignalingTokenResponse {
  token: string;
}
